import type { LanguageCode } from '../platform-languages';
import type { MsgField, MsgToken } from '../msg/tokens';

/**
 * Appointment Reminders — **rebuilt** (amendment 25). The board this replaces is
 * now titled *SUPERSEDED*.
 *
 * What was wrong with the old shape, and why this one is different:
 *
 * The settings page was an **editor** — six cards of controls plus *Content →*
 * modals, with the message bodies hidden two clicks down inside a modal that
 * could not show a preview. The messages are the thing being edited, so they now
 * live in a real two-step editor and the settings page is a **summary**.
 *
 * The old model also had no notion of **who a step goes to**. A reminder to
 * somebody who has already confirmed should not ask them to confirm; that is one
 * step with two versions, not two steps or a paragraph of explanation. Hence
 * `audience` and `twoVersions` on the step, and `bodyConfirmed` beside `body`.
 *
 * And every message now has a **language**: the practice writes English, and each
 * patient gets theirs (`platform-languages`). Nothing in this file translates —
 * it carries the mock of what the patient sees.
 */

/* ------------------------------------------------------------------ *
 * Fields — appointment fields, not recall fields.
 * ------------------------------------------------------------------ */

export type RmFieldKey =
  | 'first_name'
  | 'appointment_date'
  | 'day'
  | 'appointment_time'
  | 'provider'
  | 'appointment_type'
  | 'practice_name'
  | 'practice_address'
  | 'practice_phone'
  | 'change_link'
  | 'visit_instructions';

export const RM_FIELDS: MsgField<RmFieldKey>[] = [
  { key: 'first_name', label: 'First name', example: 'Devon', mayBeEmpty: true, defaultFallback: 'there' },
  { key: 'appointment_date', label: 'Appointment date', example: '16 October', mayBeEmpty: false },
  { key: 'day', label: 'Day', example: 'Thursday 16 October', mayBeEmpty: false },
  { key: 'appointment_time', label: 'Appointment time', example: '14:30', mayBeEmpty: false },
  { key: 'provider', label: 'Provider', example: 'Dr. Chen', mayBeEmpty: true, defaultFallback: 'your dentist' },
  { key: 'appointment_type', label: 'Appointment type', example: 'a filling', mayBeEmpty: false },
  { key: 'practice_name', label: 'Practice name', example: 'Alamo Heights Dental', mayBeEmpty: false },
  { key: 'practice_address', label: 'Practice address', example: '5500 Broadway, San Antonio', mayBeEmpty: false },
  { key: 'practice_phone', label: 'Practice phone', example: '(210) 555-0147', mayBeEmpty: false },
  { key: 'change_link', label: 'Change-the-time link', example: 'a link just for them', mayBeEmpty: false },
  {
    // Set per appointment reason, and plenty of reasons have none. The row in
    // *If a detail is missing* says so and links to where they are set.
    key: 'visit_instructions',
    label: 'Visit instructions',
    example: 'Please arrive 10 minutes early.',
    mayBeEmpty: true,
    defaultFallback: '',
  },
];

export const RM_FIELD_KEYS: ReadonlySet<string> = new Set(RM_FIELDS.map((f) => f.key));
export const RM_FIELD_BY_KEY: Record<RmFieldKey, MsgField<RmFieldKey>> = Object.fromEntries(
  RM_FIELDS.map((f) => [f.key, f]),
) as Record<RmFieldKey, MsgField<RmFieldKey>>;

export type RmToken = MsgToken<RmFieldKey>;

/* ------------------------------------------------------------------ *
 * Steps.
 * ------------------------------------------------------------------ */

export type RmChannel = 'text' | 'email' | 'ai_call';
export type RmMode = 'template' | 'smart';

export const RM_CHANNEL_LABEL: Record<RmChannel, string> = {
  text: 'Text',
  email: 'Email',
  ai_call: 'AI call',
};

export const RM_CHANNEL_ICON: Record<RmChannel, string> = {
  text: 'chat',
  email: 'mail',
  ai_call: 'phone',
};

/**
 * Who a step goes to. **Three choices, not a condition builder** — the practice
 * is choosing between three states of one appointment, and a rule engine here
 * would be a way to write rules nobody can read back.
 */
export type RmAudience = 'everyone' | 'not_confirmed' | 'confirmed';

export const RM_AUDIENCE_LABEL: Record<RmAudience, string> = {
  everyone: 'Everyone',
  not_confirmed: 'Not confirmed yet',
  confirmed: 'Confirmed',
};

/** `days before` / `hours before` — and after the visit, `hours after` / `days after`. */
export type RmUnit = 'days' | 'hours';

export interface RmStep {
  id: string;
  channel: RmChannel;
  /** How many units before the appointment (or after, in a post-op section). */
  offset: number;
  unit: RmUnit;
  mode: RmMode;
  on: boolean;
  audience: RmAudience;
  /**
   * Only meaningful under *Everyone*: one step, two bodies, and ELVA picks by the
   * appointment's status when it sends.
   */
  twoVersions?: boolean;
  subject?: RmToken[];
  subjectConfirmed?: RmToken[];
  /** The body a not-yet-confirmed patient gets — and the only body when there is one. */
  body?: RmToken[];
  /** The *Already confirmed* version, when `twoVersions`. */
  bodyConfirmed?: RmToken[];
  /** Smart mode — *What should it say?* */
  brief?: string;
  /** Smart mode — *It must include*, by field or by one of the locked items. */
  mustInclude?: string[];
  /**
   * An AI call's **text version is required**, not a backup nicety: ELVA never
   * calls twice for a post-op check, so if nobody answers this is what goes — and
   * it also goes when ELVA cannot call in the patient's language.
   */
  textVersion?: RmToken[];
  fallbacks?: Partial<Record<RmFieldKey, string>>;
}

/* ------------------------------------------------------------------ *
 * Sections. One shape, four kinds — the editor is mounted once.
 * ------------------------------------------------------------------ */

export type RmSectionKind = 'before' | 'post_op' | 'missed' | 'review';

export interface RmSection {
  /** URL segment: `before` · `missed` · `review` · a procedure key. */
  key: string;
  kind: RmSectionKind;
  /** `Before the visit` · `Implant` · `Missed appointment` · `Review ask`. */
  title: string;
  /** Page title's accent half — `Before the` / `visit`. */
  titleLead: string;
  titleAccent: string;
  sub: string;
  steps: RmStep[];
  /** Most sections take more steps; missed and review take exactly one. */
  maxSteps: number;
  on: boolean;
  /** Post-op only: what ELVA names on every check for this procedure. */
  complications?: string[];
  /** Post-op only — the practice changed the shipped default. */
  changedByPractice?: boolean;
  /** Post-op only, and `false` for a procedure that gets no checks at all. */
  hasChecks?: boolean;
}

/* ------------------------------------------------------------------ *
 * The two cards on the summary that are not sections.
 * ------------------------------------------------------------------ */

/**
 * Post-op safety (card 03). **A threshold and two pointers, not a judgement** —
 * ELVA compares two things to a rule and hands over.
 */
export interface RmPostOpSafety {
  painScoreAtOrAbove: number;
  inHours: string;
  outOfHours: string;
  /** Five things a post-op message may never do. */
  neverDo: string[];
}

/** The review ask (card 04). What varies is *when*, never *whether*. */
export interface RmReviewAsk {
  on: boolean;
  atMostPerMonths: number;
  afterCategories: string[];
  daysAfter: number;
  /** Per location, from Practice management. Read-only here. */
  destination: string;
}

/** One appointment the previews are drawn from — real ones, this week. */
export interface RmPreviewAppointment {
  id: string;
  name: string;
  confirmed: boolean;
  language: LanguageCode;
  /** `Tomorrow 10:00 · Dr. Saito · Cleaning`. */
  when: string;
  values: Partial<Record<RmFieldKey, string>>;
  /** What the patient really sees, per version, in their language. */
  rendered?: { notConfirmed?: string; confirmed?: string; spoken?: string };
}

export interface RemindersData {
  sections: RmSection[];
  safety: RmPostOpSafety;
  review: RmReviewAsk;
  /** The three appointments *How it reads* and the test page draw on. */
  previewAppointments: RmPreviewAppointment[];
  /** For the save card: `{n} appointments in the next 14 days`. */
  appointmentsNext14Days: number;
}
